import { RefreshCw, SlidersHorizontal, Filter, Wifi, WifiOff, Cloud } from "lucide-react";
import { useSync } from "@/context/SyncContext";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const RANGE_OPTIONS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "custom", label: "Custom" },
];

/**
 * DashboardHeader — title, tagline and inline utility controls.
 *
 * Right-side controls (per spec, left → right):
 *   • Branch selector (single-branch today; hidden until multi-branch ships)
 *   • Date filter
 *   • Filters (Assignee / status)
 *   • Refresh
 *   • Sync status pill
 *   • Customise dashboard
 */
export function DashboardHeader({
  title,
  subtitle,
  range,
  fromDate,
  toDate,
  onRangeChange,
  onCustomRange,
  onRefresh,
  onCustomize,
  onFilter,
  filterCount = 0,
  lastSyncedAt,
  loading = false,
}) {
  const { state, pending } = useSync();
  const [customOpen, setCustomOpen] = useState(false);
  const [tmpFrom, setTmpFrom] = useState(fromDate || "");
  const [tmpTo, setTmpTo] = useState(toDate || "");

  const syncPill = () => {
    if (state === "offline") {
      return (
        <span className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-[#e63946]/10 text-[#e63946] text-[11.5px] font-semibold">
          <WifiOff size={12} /> Offline
        </span>
      );
    }
    if (state === "syncing") {
      return (
        <span className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-[#F79009]/10 text-[#F79009] text-[11.5px] font-semibold">
          <Cloud size={12} className="animate-pulse" /> Syncing{pending ? ` (${pending})` : ""}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-[#12B76A]/10 text-[#12B76A] text-[11.5px] font-semibold">
        <Wifi size={12} /> Online
      </span>
    );
  };

  const lastSyncLabel = lastSyncedAt
    ? new Date(lastSyncedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : null;

  const applyCustom = () => {
    if (tmpFrom && tmpTo) {
      onCustomRange?.(tmpFrom, tmpTo);
      setCustomOpen(false);
    }
  };

  return (
    <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4" data-testid="dashboard-header">
      <div>
        <h1 className="text-[24px] md:text-[28px] font-bold text-[#1d3557] leading-tight" data-testid="dashboard-title">
          {title}
        </h1>
        <p className="text-[13px] text-[#667085] mt-0.5" data-testid="dashboard-subtitle">
          {subtitle}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* Date range */}
        <div className="inline-flex items-center rounded-md border border-[#E4E7EC] bg-white h-9 p-0.5">
          {RANGE_OPTIONS.map((o) => {
            const active = range === o.key;
            const isCustom = o.key === "custom";
            const button = (
              <button
                type="button"
                key={o.key}
                onClick={() => (isCustom ? setCustomOpen(true) : onRangeChange?.(o.key))}
                data-testid={`range-${o.key}`}
                className={`h-8 px-2.5 rounded text-[11.5px] font-semibold transition-colors ${
                  active
                    ? "bg-[#1d3557] text-white"
                    : "text-[#667085] hover:text-[#1d3557]"
                }`}
              >
                {o.label}
              </button>
            );
            if (isCustom) {
              return (
                <Popover key="custom" open={customOpen} onOpenChange={setCustomOpen}>
                  <PopoverTrigger asChild>{button}</PopoverTrigger>
                  <PopoverContent align="end" className="w-64 p-3">
                    <div className="text-[12px] font-semibold text-[#1d3557] mb-2">Custom date range</div>
                    <div className="space-y-2">
                      <div>
                        <label className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">From</label>
                        <input
                          type="date"
                          value={tmpFrom}
                          onChange={(e) => setTmpFrom(e.target.value)}
                          data-testid="range-custom-from"
                          className="w-full h-9 mt-1 px-2 rounded-md border border-[#E4E7EC] text-[13px]"
                        />
                      </div>
                      <div>
                        <label className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">To</label>
                        <input
                          type="date"
                          value={tmpTo}
                          onChange={(e) => setTmpTo(e.target.value)}
                          data-testid="range-custom-to"
                          className="w-full h-9 mt-1 px-2 rounded-md border border-[#E4E7EC] text-[13px]"
                        />
                      </div>
                      <Button
                        size="sm"
                        onClick={applyCustom}
                        data-testid="range-custom-apply"
                        className="w-full bg-[#1d3557] hover:bg-[#152941] text-white h-9"
                      >
                        Apply
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              );
            }
            return button;
          })}
        </div>

        {/* Filter — rendered only when onFilter is provided */}
        {onFilter && (
          <button
            type="button"
            onClick={onFilter}
            data-testid="dashboard-filter-btn"
            className="h-9 px-3 rounded-md border border-[#E4E7EC] bg-white text-[12px] font-semibold text-[#1d3557] hover:border-[#457b9d] inline-flex items-center gap-1.5"
          >
            <Filter size={13} />
            Filters
            {filterCount > 0 && (
              <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-[#e63946] text-white text-[10px] font-bold">
                {filterCount}
              </span>
            )}
          </button>
        )}

        {/* Refresh */}
        <button
          type="button"
          onClick={onRefresh}
          data-testid="dashboard-refresh"
          className="h-9 w-9 rounded-md border border-[#E4E7EC] bg-white text-[#1d3557] hover:border-[#457b9d] grid place-items-center"
          title={lastSyncLabel ? `Last updated ${lastSyncLabel}` : "Refresh"}
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>

        {/* Sync */}
        <div data-testid="dashboard-sync-pill">{syncPill()}</div>

        {/* Customize — rendered only when onCustomize is provided */}
        {onCustomize && (
          <button
            type="button"
            onClick={onCustomize}
            data-testid="dashboard-customize-btn"
            className="h-9 w-9 rounded-md border border-[#E4E7EC] bg-white text-[#1d3557] hover:border-[#457b9d] grid place-items-center"
            title="Customise dashboard"
          >
            <SlidersHorizontal size={14} />
          </button>
        )}
      </div>
    </header>
  );
}
